/**
 * Course Deploy API
 *
 * Drives the automated deploy action:
 *   1. Builds the package in the browser — for a COURSE, a pointer package
 *      (course metadata + root-relative lessonRefs; referenced-lesson-packages);
 *      for a LESSON, the one-lesson player package via the export pipeline.
 *   2. base64-encodes it and POSTs it to `POST /api/courses/:courseId/deploy`,
 *      which extracts the ZIP and hosts it per-account on S3 + CloudFront.
 *
 * The package contains NO SCORM credentials; the backend regenerates index.html
 * per account with that account's `scormRuntimeConfig`. Account credentials
 * (API_KEY/INT_KEY) are still supplied by the LMS launch query string at
 * runtime, exactly like a downloaded SCORM export.
 */

import type {
  ExportConfig,
  ExportedCourse,
  ExportSource,
  Lesson,
  LessonVersionInfo,
  ScormContentType,
} from '@course-authoring/shared-types';
import { lessonStateToExportedCourse } from '@course-authoring/shared-types';

import { buildCoursePointerZipBlob, createExportZip } from './packageBundler';
import { scanAssetsForCourses } from './assetScanner';
import { generateCourseManifest } from './manifestGenerator';
import { fetchLessonExportBundle, loadCoursePointers } from './exportSourceLoader';
import { fetchWithAuthRetry } from './apiUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Per-account runtime baked into the deployed package's index.html. This is
 * deliberately separate from `ScormPackageConfig`, which contains only fields
 * rendered into the thin LMS wrapper. API_KEY/INT_KEY are read from the launch
 * query string at runtime, not baked in here.
 */
export interface ScormRuntimeConfig {
  mode: 'emtrain';
  host: string;
  subdomain: string;
  contentId: string;
  authorAiCourseId?: string;
  contentType: ScormContentType;
  enforceSequence: boolean;
  apiBaseUrl?: string;
  rxdHostUrl?: string;
  scormDebug?: boolean;
}

export interface DeployAccountInput {
  /**
   * The hoot externalAccountId for this deploy target. Required — enforced —
   * for client accounts; the reserved base (Emtrain) scope sends `base: true`
   * instead and deploys with default branding (NA-190).
   */
  externalAccountId?: number;
  base?: boolean;
  accountSlug: string;
  scormRuntimeConfig: ScormRuntimeConfig;
  /**
   * True when the account has client-context customizations for this content
   * (from the scorm-context prefill). Flagged accounts get their own
   * account-resolved package build; unflagged accounts share the base package.
   */
  hasCustomizations?: boolean;
}

export interface DeployAccountResult {
  /** Guarded maintenance refusals are recorded by the server, including withheld parent scopes. */
  failureReporting?: 'server';
  accountSlug: string;
  /** Raw scope slug when `accountSlug` carries display text (heal rows). */
  scopeSlug?: string;
  coursePackageUrl?: string;
  success: boolean;
  error?: string;
  warning?: string;
  /**
   * Propagation provenance from the server: 'failed'/'skipped' means no
   * CloudFront purge ran — content reaches learners by cache expiry (~6 min).
   * Surfaced in the publish results so degraded delivery is never silent.
   */
  invalidation?: 'created' | 'completed' | 'skipped' | 'failed';
  /** Identity of the deploy (matches the hosted deploy-stamp.json). */
  deployId?: string;
}

export interface DeployResponse {
  results: DeployAccountResult[];
}

// ─── Package build ────────────────────────────────────────────────────────────

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read package blob.'));
        return;
      }
      // Strip the `data:...;base64,` prefix; keep only the payload.
      const commaIndex = result.indexOf(',');
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read package blob.'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Shared core: turns an already-resolved `ExportedCourse` into the base64
 * deploy package. Used by both the course path (source loaded from the server)
 * and the lesson path (assembled from the editor store).
 */
async function buildDeployPackageFromExportedCourse(params: {
  exportedCourse: ExportedCourse;
  localizedCourses?: readonly ExportedCourse[];
  source: ExportSource;
  lessonVersions: LessonVersionInfo[];
  exportedBy: string;
}): Promise<string> {
  const config: ExportConfig = {
    courseId: params.exportedCourse.id,
    source: params.source,
    enableOfflineMode: false,
  };

  const localizedCourses = params.localizedCourses ?? [params.exportedCourse];
  const assetManifest = scanAssetsForCourses(localizedCourses);
  const manifest = await generateCourseManifest({
    course: params.exportedCourse,
    source: params.source,
    lessonVersions: params.lessonVersions,
    config,
    exportedBy: params.exportedBy,
  });

  // Co-host the Rustici RXD runtime inside the deployed package so the SCORM
  // wrapper's rxdHostUrl resolves package-local (same-origin with the player) —
  // no separate Rustici host needed.
  const blob = await createExportZip(params.exportedCourse, manifest, assetManifest, config, undefined, {
    forceRusticiRuntime: true,
    localizedCourses,
  });
  return blobToBase64(blob);
}

/**
 * Builds the course deploy package as a POINTER (referenced-lesson-packages):
 * course metadata + the account-resolved lesson sequence as root-relative refs,
 * per locale, plus the player bundle and package-local Rustici runtime. No
 * lesson content is fetched or embedded — the deployed pointer resolves each
 * lesson's own hosted package at launch, within the serving account's
 * subdomain. With `accountId`, the pointer carries that account's metadata
 * overrides and lesson sequence (suppressed lessons omitted).
 */
export async function buildCourseDeployPackage(params: {
  courseId: string;
  exportedBy: string;
  accountId?: number | null;
}): Promise<string> {
  const pointers = await loadCoursePointers(params.courseId, params.accountId ?? null);
  const blob = await buildCoursePointerZipBlob(pointers);
  return blobToBase64(blob);
}

/**
 * Builds the deploy package for a single lesson, server-resolved per account:
 * the lesson state is fetched fresh (learner shape — suppressed blocks
 * omitted) rather than serialized from the editor store, so one deploy action
 * can produce a different package per selected account regardless of which
 * editing context is open. The resulting `ExportedCourse` wraps the one
 * lesson across its available locales; it deploys through the same
 * `POST /api/courses/:id/deploy` endpoint with the lesson id as the path segment.
 */
export async function buildLessonDeployPackage(params: {
  lessonId: string;
  exportedBy: string;
  accountId?: number | null;
}): Promise<string> {
  const accountId = params.accountId ?? null;
  const bundle = await fetchLessonExportBundle(params.lessonId, accountId);
  const { defaultLocale } = bundle;

  const toExportedCourse = (
    runtime: Awaited<ReturnType<typeof fetchLessonExportBundle>>['variants'][number],
    locale: string,
  ): ExportedCourse => {
    const state = runtime.contentState;
    const lesson: Lesson = {
      ...state.lessonMeta,
      blockOrder: state.blockOrder,
    };
    return {
      ...lessonStateToExportedCourse(lesson, state.blocks),
      // The zip bundler keys localized variants by course locale; lesson-wrapped
      // courses carry the lesson's locale metadata.
      locale,
      defaultLocale,
      availableLocales: state.lessonMeta.availableLocales,
      ...(runtime.narration ? { narration: runtime.narration } : {}),
    };
  };

  const localizedCourses = bundle.variants.map((runtime) => toExportedCourse(runtime, runtime.locale));

  const exportedCourse =
    localizedCourses.find((course) => course.locale === defaultLocale) ?? localizedCourses[0];
  if (!exportedCourse) {
    throw new Error('Failed to build lesson deploy package.');
  }
  return buildDeployPackageFromExportedCourse({
    exportedCourse,
    localizedCourses,
    source: 'draft',
    lessonVersions: [],
    exportedBy: params.exportedBy,
  });
}

// ─── Deploy request ───────────────────────────────────────────────────────────

/**
 * delivery-status ledger input, threaded through every deploy POST of one
 * publish fan-out. `runId` groups the POSTs into a run; `runMeta` carries the
 * run's own content identity + declared scope intent (a heal-built lesson
 * package can be the run's FIRST POST, so the run identity cannot come from
 * the request path); `packageKind` declares THIS request's package kind so
 * the server decodes the codec-ambiguous path slug with the right alphabet.
 */
export interface DeployLedgerInfo {
  runId: string;
  runMeta: {
    intendedScopeCount: number;
    initiatedAccountId: number | null;
    contentKind: 'course' | 'lesson';
    contentId: string;
    /**
     * The RUN's publish revision — carried on EVERY request so a heal-built
     * lesson POST (which can be the run's only one, if the tab dies before
     * the course POST) still initializes the run's publish-order sort key.
     */
    contentPublishedAt?: string;
  };
  packageKind: 'course' | 'lesson';
}

export interface DeployClientFailure {
  accountSlug: string;
  externalAccountId: number | null;
  error: string;
}

/**
 * Durable recording for failures that never produce a deploy POST — package
 * BUILD failures, heal refusals/withheld pointers, and run-level errors for
 * fan-outs that died before targeting. Best-effort AND time-bounded: callers
 * await this (a fire-and-forget dies with the tab), so a hung report must
 * never stall the remaining fan-out. (Ambiguous deploy-POST failures are
 * deliberately NOT reported — the server may already hold genuine outcomes.)
 */
export async function reportDeployRunFailures(
  packageId: string,
  ledger: DeployLedgerInfo,
  failures: DeployClientFailure[],
  options?: { contentPublishedAt?: string; runError?: string },
): Promise<void> {
  if (failures.length === 0 && !options?.runError) return;
  const attempt = (async () => {
    try {
      const response = await fetchWithAuthRetry(`${API_BASE_URL}/courses/${packageId}/deploy-run-failures`, {
        method: 'POST',
        signal: AbortSignal.timeout(10_000),
        body: JSON.stringify({
          runId: ledger.runId,
          runMeta: ledger.runMeta,
          packageKind: ledger.packageKind,
          failures,
          ...(options?.contentPublishedAt ? { contentPublishedAt: options.contentPublishedAt } : {}),
          ...(options?.runError ? { runError: options.runError } : {}),
        }),
      });
      if (!response.ok) {
        console.warn(`[delivery] failure recording rejected (${response.status}) — results still shown locally.`);
      }
    } catch (error) {
      console.warn('[delivery] failure recording failed (results still shown locally):', error);
    }
  })();
  // OVERALL bound, not just the request's: the auth-refresh leg inside
  // fetchWithAuthRetry has no signal, and awaited callers must never let a
  // hung refresh stall the remaining fan-out. The attempt continues in the
  // background if the timer wins.
  const timedOut = await Promise.race([
    attempt.then(() => false),
    new Promise<boolean>((resolve) => { setTimeout(() => resolve(true), 12_000); }),
  ]);
  if (timedOut) {
    console.warn('[delivery] failure recording timed out — continuing; results still shown locally.');
  }
}

/**
 * POSTs an already-built base64 package + account list to the deploy endpoint.
 * Returns the per-account results. Throws on transport/HTTP errors; per-account
 * failures are reported inside `results`.
 */
export async function deployCoursePackage(
  courseId: string,
  packageBase64: string,
  accounts: DeployAccountInput[],
  contentPublishedAt?: string,
  ledger?: DeployLedgerInfo,
): Promise<DeployResponse> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/courses/${courseId}/deploy`, {
    method: 'POST',
    body: JSON.stringify({
      packageBase64,
      accounts,
      // The publish this package was built from — stamped server-side so the
      // delivery check verifies content identity, not deploy time.
      ...(contentPublishedAt ? { contentPublishedAt } : {}),
      ...(ledger ? { runId: ledger.runId, runMeta: ledger.runMeta, packageKind: ledger.packageKind } : {}),
    }),
  });

  if (!response.ok) {
    let message = `Deploy failed (${response.status})`;
    let bodyCode: string | undefined;
    try {
      const body = (await response.json()) as { message?: unknown; code?: unknown };
      if (body && typeof body.message === 'string') message = body.message;
      if (body && typeof body.code === 'string') bodyCode = body.code;
    } catch {
      // Non-JSON error body; keep the status-based message.
    }
    // Only a provably pre-upload rejection may be recorded client-side: the
    // controller's structured DEPLOY_NOT_STARTED marker, or a 413 — payload
    // rejection (body parser or proxy) always precedes any processing. Other
    // unmarked responses (proxy 502/504 especially) can fire AFTER backend
    // side effects and are treated like ambiguous network failures.
    const error = new Error(message) as Error & { status?: number; notStarted?: boolean };
    error.status = response.status;
    error.notStarted = bodyCode === 'DEPLOY_NOT_STARTED' || response.status === 413;
    throw error;
  }

  return (await response.json()) as DeployResponse;
}

// ─── Per-account deploy orchestration ─────────────────────────────────────────

/**
 * Partitions the selected accounts by `hasCustomizations` and deploys:
 * - accounts WITHOUT customizations share ONE base package in a single batch
 *   request (identical to the pre-client-exports behavior), and
 * - each account WITH customizations gets its own account-resolved package,
 *   built and deployed sequentially.
 * Build or deploy failures are reported per account in the aggregated results
 * without aborting the remaining deploys.
 */
export function isFatalPublishingError(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 401 || status === 403 || (error as { code?: unknown } | null)?.code === 'ADMIN_AUTH_UNAVAILABLE';
}

export async function deployPackagesByAccount(params: {
  packageId: string;
  accounts: DeployAccountInput[];
  deployPackage?: typeof deployCoursePackage;
  /** Builds the base64 package; `null` = base, otherwise the account to resolve. */
  buildPackage: (externalAccountId: number | null) => Promise<string>;
  onPhase: (phase: 'building' | 'uploading') => void;
  /** The publish this content was built from — carried into the deploy stamp. */
  contentPublishedAt?: string;
  /** delivery-status run recording; omitted deploys proceed unrecorded. */
  ledger?: DeployLedgerInfo;
}): Promise<DeployResponse> {
  const baseAccounts = params.accounts.filter((account) => !account.hasCustomizations);
  const customizedAccounts = params.accounts.filter((account) => account.hasCustomizations);
  const results: DeployAccountResult[] = [];

  const failAccounts = (accounts: DeployAccountInput[], error: unknown) => {
    const message = error instanceof Error ? error.message : 'Deploy failed.';
    results.push(...accounts.map((account) => ({
      accountSlug: account.accountSlug,
      success: false,
      error: message,
    })));
  };

  const buildAndDeploy = async (
    externalAccountId: number | null,
    accounts: DeployAccountInput[],
  ) => {
    let packageBase64: string;
    try {
      params.onPhase('building');
      packageBase64 = await params.buildPackage(externalAccountId);
    } catch (error) {
      if (params.deployPackage && isFatalPublishingError(error)) throw error;
      failAccounts(accounts, error);
      // A failed BUILD never reaches the deploy endpoint — record it durably
      // (awaited: a fire-and-forget report can be cancelled with the tab).
      // The publish revision rides along so an all-failure run still sorts by
      // PUBLISH order, never behind an older successful run.
      if (params.ledger) {
        const message = error instanceof Error ? error.message : 'Deploy failed.';
        await reportDeployRunFailures(params.packageId, params.ledger, accounts.map((account) => ({
          accountSlug: account.accountSlug,
          externalAccountId: account.externalAccountId ?? null,
          error: `Package build failed: ${message}`,
        })), { contentPublishedAt: params.contentPublishedAt });
      }
      return;
    }
    try {
      params.onPhase('uploading');
      const response = await (params.deployPackage ?? deployCoursePackage)(
        params.packageId,
        packageBase64,
        accounts,
        params.contentPublishedAt,
        params.ledger,
      );
      results.push(...response.results);
    } catch (error) {
      if (params.deployPackage && isFatalPublishingError(error)) throw error;
      failAccounts(accounts, error);
      // Only a STRUCTURED pre-upload rejection (DEPLOY_NOT_STARTED) records
      // client-side. Ambiguous failures — network throws AND unmarked gateway
      // 502/504s, which can fire after backend side effects — stay
      // unrecorded: the server may hold genuine outcomes for this POST, and a
      // client failure row would eclipse them.
      const notStarted = (error as { notStarted?: unknown }).notStarted === true;
      if (params.ledger && notStarted) {
        const message = error instanceof Error ? error.message : 'Deploy failed.';
        await reportDeployRunFailures(params.packageId, params.ledger, accounts.map((account) => ({
          accountSlug: account.accountSlug,
          externalAccountId: account.externalAccountId ?? null,
          error: message,
        })), { contentPublishedAt: params.contentPublishedAt });
      }
    }
  };

  if (baseAccounts.length > 0) {
    await buildAndDeploy(null, baseAccounts);
  }
  for (const account of customizedAccounts) {
    await buildAndDeploy(account.externalAccountId ?? null, [account]);
  }

  return { results };
}
